import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Divider, Stack, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { REQUEST_PASSWORD_RESET_MUTATION } from '../../gql';
import { emailRegex, useYupValidationResolver } from '../../helpers';
import {
  Button,
  FieldLabel,
  FormField,
  SurfaceTextField,
} from '../../components/design-system';

const submitButtonStyles = {
  borderRadius: 2,
  fontSize: '1rem',
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('E-mail is mandatory field.')
    .matches(emailRegex, 'Please, provide a valid e-mail.'),
});

const PasswordResetRequestForm = () => {
  const [errors, setErrors] = useState<Array<{ error: string }>>([]);
  const [isComplete, setIsComplete] = useState(false);
  const resolver = useYupValidationResolver(validationSchema, setErrors);
  const { register, handleSubmit } = useForm({ resolver });

  const [requestPasswordReset, { loading }] = useMutation(
    REQUEST_PASSWORD_RESET_MUTATION
  );

  const onSubmit = async (values: { email: string }) => {
    const { data } = await requestPasswordReset({
      variables: { email: values.email },
    });

    if (data && !data.requestPasswordReset.errors) {
      setIsComplete(true);
    } else {
      setErrors(data!.requestPasswordReset!.errors);
    }
  };

  if (isComplete) {
    return (
      <Stack width="100%" spacing={2}>
        <Typography>
          If an account with that e-mail exists, we've sent a password reset
          link. Please check your inbox.
        </Typography>
        <Divider />
        <Stack width="100%" alignItems="center">
          <Typography variant="caption">
            <Link to="/signin" style={{ color: '#D76B90' }}>
              Back to sign-in
            </Link>
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={2}
      width="100%"
    >
      <FormField width="100%">
        <FieldLabel htmlFor="email">E-mail</FieldLabel>
        <SurfaceTextField
          fullWidth
          id="email"
          surface="sunken"
          type="text"
          placeholder="e-mail"
          {...register('email', {
            setValueAs: (value) => value.toLowerCase(),
          })}
        />
      </FormField>
      <Divider color="transparent" />
      <Button
        type="submit"
        fullWidth
        sx={submitButtonStyles}
        disabled={loading}
      >
        Request password reset
      </Button>
      <Divider />
      <Stack width="100%">
        {errors.length > 0 && (
          <Stack>
            {errors.map((item: { error: string }) => (
              <Typography color="red">{item.error}</Typography>
            ))}
          </Stack>
        )}
      </Stack>
      <Stack width="100%" alignItems="center">
        <Stack direction="row" justifyContent="center" width="100%">
          <Typography variant="caption">
            Remembered your password?{' '}
            <Link to="/signin" style={{ color: '#FF0070' }}>
              Sign-in
            </Link>
          </Typography>
        </Stack>
        <Typography variant="caption">
          <Link to="/" style={{ color: '#D76B90' }}>
            Home
          </Link>
        </Typography>
      </Stack>
    </Stack>
  );
};

export { PasswordResetRequestForm };
